import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, m } from 'motion/react';
import { ChevronDown, LogOut, MessageSquareWarning, Settings } from 'lucide-react';
import { apiPost } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/cn';
import { MonthSelector } from './MonthSelector';
import { EyeToggle } from './EyeToggle';
import { ThemeToggle } from './ThemeToggle';

const EASE = [0.16, 1, 0.3, 1] as const;

/** Contexto global persistente: período, privacidade, tema e perfil. */
export function Header({ onSupport }: { onSupport: () => void }) {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleLogout = async () => {
    setOpen(false);
    try {
      await apiPost('/auth/logout');
    } catch {
      /* A sessão local também deve terminar se a API estiver indisponível. */
    }
    queryClient.clear();
    clear();
    navigate('/login');
  };

  const initial = (user?.name.trim().charAt(0) ?? '?').toUpperCase();

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-20 border-b border-border/70 bg-canvas/95 pt-[env(safe-area-inset-top)] backdrop-blur-xl"
    >
      <div className="relative flex h-14 min-w-0 items-center px-3 sm:px-4 lg:h-[4.5rem] lg:px-8">
        <div className="flex items-center gap-2 lg:hidden" aria-label="Lira">
          <span className="grid size-9 place-items-center rounded-xl bg-primary text-sm font-bold text-white shadow-card">
            L
          </span>
          <span className="text-sm font-semibold tracking-tight text-ink">Lira</span>
        </div>

        <div data-testid="desktop-period-selector" className="hidden lg:ml-8 lg:block">
          <MonthSelector />
        </div>

        <div
          data-testid="mobile-header-actions"
          className="ml-auto flex min-w-0 items-center justify-end gap-0.5 sm:gap-1 lg:gap-2"
        >
          <button
            type="button"
            onClick={onSupport}
            aria-label="Reportar problema"
            title="Reportar problema"
            className="grid size-11 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-surface hover:text-primary focus-visible:ring-2 focus-visible:ring-primary lg:hidden"
          >
            <MessageSquareWarning className="size-5" />
          </button>
          <ThemeToggle />
          <EyeToggle />

          <div ref={menuRef} className="relative shrink-0">
            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label="Abrir menu de perfil"
              className="flex size-11 items-center justify-center rounded-full transition-colors hover:bg-surface focus-visible:ring-2 focus-visible:ring-primary lg:size-auto lg:gap-2 lg:py-1 lg:pl-1 lg:pr-2"
            >
              <span className="grid size-8 place-items-center rounded-full bg-primary-weak text-sm font-semibold text-primary">
                {initial}
              </span>
              <span className="hidden max-w-40 truncate text-sm font-medium text-ink sm:block">
                {user?.name}
              </span>
              <ChevronDown
                className={cn('hidden size-4 text-muted transition-transform lg:block', open && 'rotate-180')}
              />
            </button>

            <AnimatePresence>
              {open && (
                <m.div
                  initial={{ opacity: 0, y: -6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.98 }}
                  transition={{ duration: 0.15, ease: EASE }}
                  className="absolute right-0 top-full z-30 mt-2 min-w-56 overflow-hidden rounded-2xl border border-border bg-surface shadow-hover"
                  role="menu"
                >
                  <div className="px-4 py-3">
                    <p className="truncate font-medium text-ink">{user?.name}</p>
                    <p className="truncate text-sm text-muted">{user?.email}</p>
                  </div>
                  <div className="h-px bg-border" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      navigate('/config');
                    }}
                    className="flex min-h-11 w-full items-center gap-2 px-4 py-3 text-sm font-medium text-ink transition-colors hover:bg-canvas"
                  >
                    <Settings className="size-4 text-muted" />
                    Configurações
                  </button>
                  <div className="h-px bg-border" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void handleLogout()}
                    className="flex min-h-11 w-full items-center gap-2 px-4 py-3 text-sm font-medium text-expense transition-colors hover:bg-canvas"
                  >
                    <LogOut className="size-4" />
                    Sair
                  </button>
                </m.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div
        data-testid="mobile-period-bar"
        className="flex min-w-0 justify-center px-3 pb-2 lg:hidden"
      >
        <MonthSelector />
      </div>
    </header>
  );
}
